import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, matchPath, useLocation } from "react-router-dom";
import { MotionConfig, MotionGlobalConfig } from "framer-motion";
import "@/App.css";
// Build-time snapshot (scripts/prerender.js sets window.__PRERENDER__): every animation completes instantly and
// Reveal/MaskLines render their final state, so no mid-animation opacity/transform ever reaches the static HTML.
const PRERENDER = typeof window !== "undefined" && !!window.__PRERENDER__;
if (PRERENDER) MotionGlobalConfig.skipAnimations = true;
import { Nav, Footer, MobileCallBar, ScrollTop } from "@/components/site/Layout";
import { CookieConsent } from "@/components/site/CookieConsent";
import { installClickTracking, reportPhoneCallConversion } from "@/lib/analytics";
import { getEstimateConfig } from "@/lib/estimateApi";
import { preloadCost } from "@/lib/costApi";
import { afterLoadIdle } from "@/lib/idle";

// Every page is its own chunk: [route pattern, loader, props]. The first pattern that matches a URL wins, so the
// catch-all stays last. The site shell (nav, footer, consent, analytics) is all main.js carries.
const estimate = () => import(/* webpackChunkName: "page-estimate" */ "@/pages/Estimate");
const legal = () => import(/* webpackChunkName: "page-legal" */ "@/pages/Legal");
const PAGES = [
  ["/", () => import(/* webpackChunkName: "page-home" */ "@/pages/Home")],
  ["/about", () => import(/* webpackChunkName: "page-about" */ "@/pages/About")],
  ["/services", () => import(/* webpackChunkName: "page-services" */ "@/pages/Services")],
  ["/services/:slug", () => import(/* webpackChunkName: "page-service" */ "@/pages/ServiceDetail")],
  ["/projects", () => import(/* webpackChunkName: "page-projects" */ "@/pages/Projects")],
  ["/projects/:slug", () => import(/* webpackChunkName: "page-project" */ "@/pages/ProjectDetail")],
  ["/service-areas", () => import(/* webpackChunkName: "page-areas" */ "@/pages/ServiceAreas")],
  ["/service-areas/:slug", () => import(/* webpackChunkName: "page-city" */ "@/pages/CityDetail")],
  ["/process", () => import(/* webpackChunkName: "page-process" */ "@/pages/Process")],
  ["/financing", () => import(/* webpackChunkName: "page-financing" */ "@/pages/Financing")],
  ["/reviews", () => import(/* webpackChunkName: "page-reviews" */ "@/pages/Reviews")],
  ["/careers", () => import(/* webpackChunkName: "page-careers" */ "@/pages/Careers")],
  ["/contact", () => import(/* webpackChunkName: "page-contact" */ "@/pages/Contact")],
  ["/trade-partners", () => import(/* webpackChunkName: "page-trade" */ "@/pages/TradePartners")],
  ["/blog", () => import(/* webpackChunkName: "page-blog" */ "@/pages/Blog")],
  ["/blog/:slug", () => import(/* webpackChunkName: "page-post" */ "@/pages/BlogPost")],
  ["/faq", () => import(/* webpackChunkName: "page-faq" */ "@/pages/Faq")],
  ["/estimate", estimate],
  ["/embed/estimate", estimate, { embed: true }],
  ["/design", () => import(/* webpackChunkName: "page-design" */ "@/pages/DesignStudio")],
  ["/my-designs", () => import(/* webpackChunkName: "page-my-designs" */ "@/pages/MyDesigns")],
  ["/review", () => import(/* webpackChunkName: "page-review-link" */ "@/pages/ReviewLink")],
  ["/admin/*", () => import(/* webpackChunkName: "page-admin" */ "@/pages/Admin")],
  ["/cost", () => import(/* webpackChunkName: "page-cost" */ "@/pages/cost/CostHub")],
  ["/cost/:type", () => import(/* webpackChunkName: "page-cost-type" */ "@/pages/cost/CostType")],
  ["/cost/:type/:metro", () => import(/* webpackChunkName: "page-cost-metro" */ "@/pages/cost/CostMetro")],
  ["/privacy", legal, { kind: "privacy" }],
  ["/terms", legal, { kind: "terms" }],
  ["/accessibility", legal, { kind: "accessibility" }],
  ["*", () => import(/* webpackChunkName: "page-not-found" */ "@/pages/NotFound")],
];
const ROUTE_DATA = { "/estimate": getEstimateConfig, "/embed/estimate": getEstimateConfig };
const preloaded = {};
const withTimeout = (p, ms) => Promise.race([p, new Promise((r) => setTimeout(r, ms))]);
const routeKey = (pathname) => PAGES.find(([pattern]) => matchPath(pattern, pathname))[0];
const loadPage = (key) => PAGES.find(([pattern]) => pattern === key)[1]().then((m) => { preloaded[key] = m; return m; });
// Lazy routes WITHOUT <Suspense>: a Suspense boundary needs a `<!--$-->` marker in the HTML, which the prerender (a client
// render snapshot) never has, so hydrating through one always fails and React rebuilds the whole page. index.js awaits
// `preloadRoute(path)` (chunk + the data the first render needs) before hydrating, so the hydration render is synchronous
// and adopts the prerendered DOM; the prerendered HTML also preloads the chunk so it downloads alongside main.js.
export const preloadRoute = (path) => Promise.allSettled([
  loadPage(routeKey(path)),
  ROUTE_DATA[path] && withTimeout(ROUTE_DATA[path](), 4000),
  path.startsWith("/cost") && withTimeout(preloadCost(path, window.location.search), 4000),
]);

// Public site chrome; the private /admin area, the chromeless /embed routes and the /review short link render without it.
function SiteChrome({ part }) {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin") || pathname.startsWith("/embed") || pathname === "/review") return null;
  if (part === "nav") return <Nav />;
  return (<><Footer /><MobileCallBar /><LazyTextWidget /><CookieConsent /></>);
}

// The chat/contact widget (framer-motion panel) is fetched only once the visitor scrolls, clicks or taps, or 5 s after
// load — never in main.js and never in the prerendered HTML.
function LazyTextWidget() {
  const [Widget, setWidget] = useState(null);
  useEffect(() => {
    if (PRERENDER) return undefined;
    let alive = true;
    const events = ["scroll", "pointerdown", "keydown", "touchstart"];
    const go = () => { events.forEach((ev) => window.removeEventListener(ev, go)); clearTimeout(t); import(/* webpackChunkName: "text-widget" */ "@/components/site/TextWidget").then((m) => { if (alive) setWidget(() => m.TextWidget); }); };
    const t = setTimeout(go, 5000);
    events.forEach((ev) => window.addEventListener(ev, go, { passive: true, once: true }));
    return () => { alive = false; clearTimeout(t); events.forEach((ev) => window.removeEventListener(ev, go)); };
  }, []);
  return Widget ? <Widget /> : null;
}

// Client-side navigations show the dark shell until the chunk arrives (usually already warmed by onLinkIntent).
// Every route renders this component at the same tree position, so React reuses one instance across navigations: the
// state is keyed by `path` and re-resolved whenever the matched route changes (a stale module must never survive).
function LazyPage({ path, props }) {
  const [state, setState] = useState(() => ({ path, mod: preloaded[path] || null }));
  const mod = state.path === path ? state.mod : preloaded[path] || null;
  useEffect(() => {
    if (state.path === path && state.mod) return undefined;
    let live = true;
    loadPage(path).then((m) => { if (live) setState({ path, mod: m }); });
    return () => { live = false; };
  }, [path, state]);
  if (!mod) return <div className="min-h-dvh bg-[#111]" />;
  const Page = mod.default;
  return <Page {...props} />;
}

// Toasts only ever follow a user action, so the toaster (sonner) arrives after load + idle instead of riding in main.js.
function LazyToaster() {
  const [Toaster, setToaster] = useState(null);
  useEffect(() => {
    if (PRERENDER) return undefined;
    return afterLoadIdle(() => import(/* webpackChunkName: "toaster" */ "sonner").then((m) => setToaster(() => m.Toaster)));
  }, []);
  return Toaster ? <Toaster position="bottom-right" richColors /> : null;
}

// Sitewide tel: click → Google Ads conversion. Plain left-clicks only; navigation completes via the helper.
function onTelClick(e) {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  const a = e.target.closest && e.target.closest('a[href^="tel:"]');
  if (!a || a.target === "_blank") return;
  e.preventDefault();
  reportPhoneCallConversion(a.href);
}

// Warm the chunk of an internal link the visitor hovers or touches, so most navigations never show the shell placeholder.
function onLinkIntent(e) {
  const a = e.target.closest && e.target.closest('a[href^="/"]');
  if (!a || a.target) return;
  const key = routeKey(a.pathname.replace(/\/+$/, "") || "/");
  if (!preloaded[key]) loadPage(key).catch(() => {});
}

function App() {
  useEffect(() => {
    document.addEventListener("click", onTelClick);
    document.addEventListener("pointerover", onLinkIntent);
    document.addEventListener("touchstart", onLinkIntent, { passive: true });
    const uninstall = installClickTracking();
    return () => { uninstall(); document.removeEventListener("click", onTelClick); document.removeEventListener("pointerover", onLinkIntent); document.removeEventListener("touchstart", onLinkIntent); };
  }, []);

  useEffect(() => {
    // Native scrolling on touch devices and for reduced-motion users; Lenis only smooths desktop wheel scrolling, and it
    // is fetched and started after load + idle so it never competes with the first paint.
    if (PRERENDER || "ontouchstart" in window || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    let alive = true, lenis, raf;
    const cancel = afterLoadIdle(() => import(/* webpackChunkName: "lenis" */ "lenis").then(({ default: Lenis }) => {
      if (!alive) return;
      lenis = new Lenis({ duration: 1.1, smoothWheel: true });
      window.__lenis = lenis;
      const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
      raf = requestAnimationFrame(loop);
    }));
    return () => { alive = false; cancel(); cancelAnimationFrame(raf); lenis?.destroy(); window.__lenis = null; };
  }, []);

  return (
    <MotionConfig reducedMotion={PRERENDER ? "always" : "user"}>
    <div className="App bg-[#111] grain min-h-dvh">
      <BrowserRouter>
        <ScrollTop />
        <SiteChrome part="nav" />
        <Routes>
          {PAGES.map(([pattern, , props]) => <Route key={pattern} path={pattern} element={<LazyPage key={pattern} path={pattern} props={props} />} />)}
        </Routes>
        <SiteChrome part="footer" />
      </BrowserRouter>
      <LazyToaster />
    </div>
    </MotionConfig>
  );
}

export default App;
